import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, type User } from '../services/user.service';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

export const UserManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Menu state for actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Role change dialog
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<'user' | 'admin'>('user');

  // Alert state
  const [alertMessage, setAlertMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Fetch users
  const {
    data: users,
    isLoading,
    error,
  } = useQuery<User[]>({
    queryKey: ['adminUsers'],
    queryFn: userService.getAll,
  });

  // Change role mutation
  const changeRoleMutation = useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: 'user' | 'admin';
    }) => userService.changeRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setAlertMessage({ type: 'success', text: t('admin.users.roleChanged') });
      setRoleDialogOpen(false);
    },
    onError: () => {
      setAlertMessage({
        type: 'error',
        text: t('admin.users.roleChangeError'),
      });
    },
  });

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({
      userId,
      isActive,
    }: {
      userId: string;
      isActive: boolean;
    }) =>
      isActive ? userService.activate(userId) : userService.deactivate(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setAlertMessage({
        type: 'success',
        text: t('admin.users.statusChanged'),
      });
    },
    onError: () => {
      setAlertMessage({
        type: 'error',
        text: t('admin.users.statusChangeError'),
      });
    },
  });

  const handleMenuOpen = (event: React.MouseEvent, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRoleDialogOpen = () => {
    if (selectedUser) {
      setNewRole(selectedUser.role);
      setRoleDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleRoleChange = () => {
    if (selectedUser) {
      changeRoleMutation.mutate({ userId: selectedUser.id, role: newRole });
    }
  };

  const handleToggleActive = () => {
    if (selectedUser) {
      toggleActiveMutation.mutate({
        userId: selectedUser.id,
        isActive: !selectedUser.isActive,
      });
    }
    handleMenuClose();
  };

  // Filter users by search query
  const filteredUsers = users?.filter(
    user =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Paginate
  const paginatedUsers = filteredUsers?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{t('admin.users.loadError')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/admin"
          underline="hover"
          color="inherit"
        >
          {t('admin.dashboard.title')}
        </Link>
        <Typography color="text.primary">{t('admin.users.title')}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        {t('admin.users.title')}
      </Typography>

      {alertMessage && (
        <Alert
          severity={alertMessage.type}
          sx={{ mb: 3 }}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('admin.users.search')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Users Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.users.user')}</TableCell>
                <TableCell>{t('admin.users.email')}</TableCell>
                <TableCell>{t('admin.users.role')}</TableCell>
                <TableCell>{t('admin.users.status')}</TableCell>
                <TableCell>{t('admin.users.lastLogin')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers?.map(user => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={user.avatar} sx={{ width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      <Typography>{user.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        user.role === 'admin' ? <AdminIcon /> : <PersonIcon />
                      }
                      label={
                        user.role === 'admin'
                          ? t('user.roleAdmin')
                          : t('user.roleUser')
                      }
                      size="small"
                      color={user.role === 'admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={user.isActive ? <ActiveIcon /> : <BlockIcon />}
                      label={
                        user.isActive
                          ? t('admin.users.active')
                          : t('admin.users.inactive')
                      }
                      size="small"
                      color={user.isActive ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.lastLogin)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={e => handleMenuOpen(e, user)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredUsers?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage={t('common.rowsPerPage')}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => navigate(`/admin/users/${selectedUser?.id}`)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.viewDetails')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRoleDialogOpen}>
          <ListItemIcon>
            <AdminIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('admin.users.changeRole')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleToggleActive}>
          <ListItemIcon>
            {selectedUser?.isActive ? (
              <BlockIcon fontSize="small" />
            ) : (
              <ActiveIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedUser?.isActive
              ? t('admin.users.deactivate')
              : t('admin.users.activate')}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)}>
        <DialogTitle>{t('admin.users.changeRoleTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('admin.users.changeRoleDescription', {
              name: selectedUser?.name,
            })}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{t('admin.users.role')}</InputLabel>
            <Select
              value={newRole}
              label={t('admin.users.role')}
              onChange={e => setNewRole(e.target.value as 'user' | 'admin')}
            >
              <MenuItem value="user">{t('user.roleUser')}</MenuItem>
              <MenuItem value="admin">{t('user.roleAdmin')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRoleChange}
            variant="contained"
            disabled={changeRoleMutation.isPending}
          >
            {changeRoleMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('common.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
